'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { useTranslation } from '@/hooks/client/use-translation'
import SvgMagnifyingGlass from '@/icons/regular/MagnifyingGlass'
import SvgXmark from '@/icons/regular/Xmark'
import { tw } from '@/lib/utils'

interface SearchFieldProps {
  value: string
  onChange: (term: string) => void
  className?: string
}

const DEBOUNCE_MS = 300

// Brand / model name search. The field is a plain 16 px input (no iOS zoom),
// debounced into the reducer; a clear button appears once there is text.
export const SearchField = ({ value, onChange, className }: SearchFieldProps) => {
  const { t } = useTranslation()
  const [local, setLocal] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const adoptedInput = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    setLocal(value)
  }, [value])

  useEffect(() => {
    if (adoptedInput.current) {
      return
    }
    adoptedInput.current = true
    // The SSR input accepts typing before React attaches its handler. React
    // preserves that DOM value on hydration; adopt it into the filter state.
    const typed = inputRef.current?.value
    if (typed && typed !== value) {
      setLocal(typed)
      onChange(typed)
    }
  }, [onChange, value])

  const handleChange = useCallback(
    (next: string) => {
      setLocal(next)
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
      timerRef.current = setTimeout(() => onChange(next), DEBOUNCE_MS)
    },
    [onChange]
  )

  return (
    <div className={tw('relative', className)}>
      <label htmlFor="directory-search" className="sr-only">
        {t('directory.search.label', 'Search hearing aids')}
      </label>
      <SvgMagnifyingGlass
        aria-hidden
        className="fill-navy/50 pointer-events-none absolute top-1/2 left-3.5 h-[18px] w-[18px] -translate-y-1/2"
      />
      <input
        ref={inputRef}
        id="directory-search"
        type="search"
        name="search"
        autoComplete="off"
        placeholder={t('directory.search.placeholder', 'Search brand or model')}
        value={local}
        onChange={(e) => handleChange(e.target.value)}
        className={tw(
          'border-navy/20 text-navy placeholder:text-navy/45 focus:border-lapis focus-visible:ring-lapis/30 min-h-[48px] w-full rounded-xl border bg-white pl-10 text-[16px] transition-colors focus:outline-none focus-visible:ring-2 [&::-webkit-search-cancel-button]:hidden',
          local ? 'pr-11' : 'pr-3'
        )}
      />
      {local ? (
        <button
          type="button"
          data-tracking="ignore"
          aria-label={t('directory.search.clear', 'Clear search')}
          onClick={() => {
            if (timerRef.current) {
              clearTimeout(timerRef.current)
            }
            setLocal('')
            onChange('')
          }}
          className="hover:bg-navy/5 text-navy absolute top-1/2 right-2 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full"
        >
          <SvgXmark className="h-4 w-4 fill-current" aria-hidden />
        </button>
      ) : null}
    </div>
  )
}
